import { resolveAvatarRules } from './avatarRules';
import { normalizeSavedDeckText } from '../savedDeckRestore';
import { resolvePrinting } from '../sorcery/foil';
import { k } from '../../localization/t';

function makeUidFactory(prefix) {
  let counter = 1;
  return () => `${prefix}-${counter++}-${Math.random().toString(36).slice(2, 7)}`;
}

const uid = makeUidFactory('card');
const tokenUid = makeUidFactory('token');
const diceUid = makeUidFactory('dice');

export function createTokenInstance(x, z, color = 'red') {
  return {
    id: tokenUid(),
    x,
    z,
    color,
  };
}

export function createDiceInstance(x, z, dieType = 'd6') {
  const faceCount = { d4: 4, d6: 6, d8: 8, d10: 10, d12: 12, d20: 20 }[dieType] || 6;
  return {
    id: diceUid(),
    x,
    z,
    dieType,
    value: Math.ceil(Math.random() * faceCount),
  };
}

// Starting life is parameterized so the ModeSetupDialog's
// "Quick Match (10 HP) vs Standard (20 HP)" pick can flow down to
// the match. Both players start at the same value. The rule lives
// on the instance, not per-player, so they can't disagree.
export function createTrackerState(startingLife = 20) {
  return {
    p1: { life: startingLife, mana: 0, earth: 0, water: 0, fire: 0, wind: 0 },
    p2: { life: startingLife, mana: 0, earth: 0, water: 0, fire: 0, wind: 0 },
  };
}

export function createGameState(startingLife = 20) {
  return {
    tableCards: [],
    handCards: [],
    piles: [],
    tokens: [],
    dice: [],
    // Owner-locked unit hands, keyed by the owner unit's instance id.
    // See engine/core/unitHands.js. "Only It Can Cast" spells.
    unitHands: {},
    trackers: createTrackerState(startingLife),
  };
}

export function shuffleArray(array) {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function createCardInstance(card, printing, rotated = false, stableId = null) {
  // Store the image path (not full URL) so it works across different local proxy ports
  const fullUrl = printing?.image_url || card.printings?.[0]?.image_url || '';
  const imagePath = fullUrl.replace(/^https?:\/\/[^/]+/, '');

  // Two-sided cards (Druid avatar etc.) carry both faces on the
  // instance so the gameboard can flip the mesh in place when the
  // card's in-game flip ability fires. `face` tracks which side is
  // currently visible; `backFaceImagePath` is the mesh's swap-in
  // texture. Non-flippable cards leave these undefined.
  const backImageUrl = card.backFace?.printings?.[0]?.image_url || '';
  const backImagePath = backImageUrl.replace(/^https?:\/\/[^/]+/, '');

  return {
    // Use the caller-supplied stable id when present. The server assigns
    // these for match decks so both clients produce the same mesh ids for
    // the same cards. Falls back to a local uid for single-player sessions.
    id: stableId || uid(),
    cardId: card.unique_id,
    name: card.name,
    imageUrl: fullUrl,
    imagePath,
    printingId: printing?.unique_id ?? null,
    foiling: printing?.foiling || 'S',
    type: card.type || card.types?.[0] || '',
    isSite: card.played_horizontally || card.type === 'Site',
    // Sorcery subtypes (Undead, Mortal, Giant, Dwarf, Spellcaster…).
    // copied from the catalog's normalized `traits` array. The scope
    // resolver's filter.subtype check needs this; without it auras like
    // Death Knight's "+1 to other nearby Undead allies" silently fail
    // because cardInstance.subTypes is empty. v1.19.
    subTypes: Array.isArray(card.traits) ? [...card.traits] : [],
    // v1.92. Flat lowercase element list so scope filter.element
    // works on instances. Sites have one entry (Earth / Water / Fire /
    // Air); minions can have one or more. Avatar of Earth's per-each
    // counts nearby earth sites via this.
    elements: Array.isArray(card.elements)
      ? card.elements.map((e) => String(e?.name || e?.id || e).toLowerCase())
      : [],
    tapped: false,
    faceDown: false,
    rotated,
    x: 0,
    y: 0,
    z: 0,
    ...(card.backFace ? {
      face: 'front',
      backFaceImageUrl: backImageUrl,
      backFaceImagePath: backImagePath,
    } : {}),
  };
}

export function createPile(name, cards, x, z, rotated = false) {
  return {
    id: `${name.toLowerCase()}-${rotated ? 'p2' : 'p1'}`,
    name,
    cards,
    x,
    z,
    rotated,
  };
}

export function spawnDeck(deck, sorceryCards, spawnPoints = {}, rotated = false) {
  const cardIndex = new Map();
  const cardByNormalizedName = new Map();
  for (const card of sorceryCards || []) {
    cardIndex.set(card.unique_id, card);
    const normalizedName = normalizeSavedDeckText(card.name);
    if (normalizedName && !cardByNormalizedName.has(normalizedName)) {
      cardByNormalizedName.set(normalizedName, card);
    }
  }

  // Resolve a saved deck entry to a catalog card. The primary key is the
  // unique_id slug, but slugs can drift between save and spawn. E.g.
  // "King's Council" normalises to sorcery-king_s_council in one path and
  // sorcery-kings_council in another (apostrophe split vs stripped). When
  // the slug misses, fall back to the entry's name (matching the deck
  // builder's findSavedDeckCard) so punctuation cards don't vanish.
  const resolveCard = (savedCard) =>
    cardIndex.get(savedCard?.cardId) ||
    cardByNormalizedName.get(normalizeSavedDeckText(savedCard?.cardName)) ||
    null;

  // Detect a server-prepared match deck: if cards already carry instanceIds
  // the server has shuffled and assigned stable ids. We preserve that order
  // verbatim so both clients produce identical pile layouts and mesh ids.
  const isServerPrepared = Array.isArray(deck.cards) && deck.cards.length > 0 &&
    typeof deck.cards[0]?.instanceId === 'string';

  // Host-avatar pre-scan. Decks that carry MULTIPLE Avatar entries
  // (Imposter is the only legal case today) must spawn the host.
  // the Avatar whose deckbuilding rules allow extras. As the primary
  // playmat avatar regardless of how the persisted zone field looks.
  // The matchmaking server is free to shuffle, strip, or normalise
  // zones between save and spawn; this scan is independent of that
  // pipeline so the wrong face can't end up on the table when the
  // deck has more than one Avatar to choose from.
  //
  // The same scan also picks up `forbidAtlas` (Magician): atlas-bound
  // entries must collapse into the spellbook BEFORE routing, otherwise
  // a stale `zone: 'atlas'` or an untagged Site gets pushed onto a
  // pile we then refuse to create. Silently dropping cards from the
  // deck. Only the PRIMARY avatar's rule matters here; an Imposter
  // deck that parked a Magician as an extra would still need the atlas.
  let hostAvatarCardId = null;
  let firstAvatarCardId = null;
  for (const savedCard of deck.cards || []) {
    const card = resolveCard(savedCard);
    if (!card || card.type !== 'Avatar') continue;
    if (!firstAvatarCardId) firstAvatarCardId = savedCard.cardId;
    const rules = resolveAvatarRules(card);
    if (rules.deckbuilding?.allowExtraAvatars && !hostAvatarCardId) {
      hostAvatarCardId = savedCard.cardId;
    }
  }
  const primaryAvatarCardId = hostAvatarCardId || firstAvatarCardId;
  const primaryAvatarForbidAtlas = !!resolveAvatarRules(
    primaryAvatarCardId ? cardIndex.get(primaryAvatarCardId) : null,
  ).deckbuilding?.forbidAtlas;

  const spellbookCards = [];
  const atlasCards = [];
  const collectionCards = [];
  let avatarInstance = null;

  for (const savedCard of deck.cards || []) {
    const card = resolveCard(savedCard);
    if (!card) continue;

    const printing = resolvePrinting(card, savedCard.printingId, savedCard.foiling ?? 'S') || card.printings?.[0];
    const instance = createCardInstance(card, printing, rotated, savedCard.instanceId || null);
    const isAvatarCard = card.type === 'Avatar' || card._sorceryCategory === 'Avatar';
    const isSiteCard = card.type === 'Site' || card._sorceryCategory === 'Site';
    let zone = savedCard.zone;

    // When a host avatar (Imposter) is present, override zone routing
    // for Avatar entries: the host takes the playmat slot and every
    // other Avatar is forced to the spellbook regardless of what zone
    // the server / saved data claims. This is the multiplayer-spawn
    // fix. Extras can ride a stale `zone: 'avatar'` field over the
    // wire, but the host always wins.
    if (hostAvatarCardId && isAvatarCard) {
      if (savedCard.cardId === hostAvatarCardId) {
        zone = 'avatar';
      } else if (zone === 'collection' || savedCard.isSideboard) {
        // Collection-bound extra. Imposter's Mask ability banishes an
        // Avatar *from the Collection*, so these must land there. The
        // server's deck normaliser drops the per-card `zone` on save and
        // keeps only `isSideboard`, so that flag is the signal that
        // actually survives into a ranked match. Honour it too. Set
        // 'collection' explicitly rather than leaving the zone undefined,
        // or the `!zone && isAvatarCard` branch below would let the extra
        // claim the playmat slot.
        zone = 'collection';
      } else {
        // Any other non-host avatar rides off the playmat into the
        // spellbook (covers stale `zone: 'avatar'` from the wire).
        zone = 'spellbook';
      }
    }

    // Honour the persisted zone first. Imposter decks can carry extra
    // avatars in the spellbook or collection, and those must not be
    // hoisted into the avatar slot just because their card type is
    // Avatar. Legacy / starter decks saved before per-entry zones still
    // work because (a) they don't have multiple avatars and (b) the
    // type-based fallback below catches them.
    //
    // The `!avatarInstance` guard is critical: legacy decks (or decks
    // saved during the Imposter zone-routing regression) can contain
    // multiple entries with `zone === 'avatar'`. Without the guard the
    // later entry would clobber the primary and the wrong face would
    // spawn on the playmat. Extras fall through to spellbook/atlas/
    // collection so no card is lost.
    const claimsAvatarSlot = (zone === 'avatar' || (!zone && isAvatarCard))
      && !avatarInstance;
    if (claimsAvatarSlot) {
      avatarInstance = instance;
    } else if (zone === 'collection' || (!zone && savedCard.isSideboard)) {
      collectionCards.push(instance);
    } else if (zone === 'atlas' || (!zone && isSiteCard)) {
      // Magician (forbidAtlas) has no atlas. Every site folds into the
      // spellbook so it can be drawn alongside spells. Without this
      // redirect a saved `zone: 'atlas'` or an untagged Site silently
      // disappears since the Atlas pile is never created.
      if (primaryAvatarForbidAtlas) {
        spellbookCards.push(instance);
      } else {
        atlasCards.push(instance);
      }
    } else {
      // 'spellbook' (explicit), legacy non-site/non-avatar entries, and
      // Imposter's extra avatars whose saved zone is 'spellbook'.
      spellbookCards.push(instance);
    }
  }

  // Auto-detect collection: if spellbook has more than 60 cards,
  // the extras are likely collection (deck max is 60 spellbook)
  const SPELLBOOK_MAX = 60;
  if (collectionCards.length === 0 && spellbookCards.length > SPELLBOOK_MAX) {
    const overflow = spellbookCards.splice(SPELLBOOK_MAX);
    collectionCards.push(...overflow);
  }

  const sb = spawnPoints.spellbook || { x: 30, z: 30 };
  const at = spawnPoints.atlas || { x: 30, z: -30 };
  const av = spawnPoints.avatar || { x: -30, z: 0 };
  const co = spawnPoints.collection || { x: -30, z: 30 };

  // Only shuffle client-side for non-server-prepared decks (single-player,
  // saved session loads). Server-prepared decks are already shuffled.
  const orderedSpellbook = isServerPrepared ? spellbookCards : shuffleArray(spellbookCards);
  const orderedAtlas = isServerPrepared ? atlasCards : shuffleArray(atlasCards);

  // Magician: `forbidAtlas` collapses the two-deck split. Every card
  // (sites included) is already in `spellbookCards` (the routing loop
  // redirected atlas-zoned entries). Suppress the Atlas pile so an
  // empty pile mesh doesn't render and the start-phase draw collapses
  // to one option, and stamp every instance with `_spellbookBack` so a
  // site drawn into hand doesn't leak its type via the atlas back.
  if (primaryAvatarForbidAtlas) {
    for (const c of orderedSpellbook) c._spellbookBack = true;
  }

  const spellbookPile = createPile(k('Spellbook'), orderedSpellbook, sb.x, sb.z, rotated);
  const piles = [spellbookPile];
  if (!primaryAvatarForbidAtlas) {
    piles.push(createPile(k('Atlas'), orderedAtlas, at.x, at.z, rotated));
  }

  if (collectionCards.length > 0) {
    const collectionPile = createPile(k('Collection'), collectionCards, co.x, co.z, rotated);
    piles.push(collectionPile);
  }

  const result = { piles };

  if (avatarInstance) {
    avatarInstance.x = av.x;
    avatarInstance.z = av.z;
    result.avatarCard = avatarInstance;
  }

  return result;
}

// A single identity-free placeholder card for an opponent's hidden pile. It
// carries ONLY what a face-down pile mesh needs (rotation + a draw-pile back)
// and NOTHING that could leak the opponent's deck. No cardId, no name, no
// imageUrl, no type. `_facedownPlaceholder` marks it so no code path ever
// treats it as a real card. Sites get `isSite: true` purely so the pile mesh
// draws the site-back; that is a public fact (the opponent's atlas size).
export function createPlaceholderCard(rotated, isSite) {
  return {
    id: `oppph-${rotated ? 'p2' : 'p1'}-${uid()}`,
    _facedownPlaceholder: true,
    isSite: !!isSite,
    faceDown: true,
    rotated,
    x: 0,
    y: 0,
    z: 0,
  };
}

// Build the OPPONENT's draw piles from COUNTS ONLY (R2b true hidden info). The
// server withholds the opponent's deck contents + order; the client receives
// `{ spellbook, atlas, collection, total }` and renders each pile as a stack of
// N identity-free face-down placeholder cards. The pile mesh sizes itself by
// `pile.cards.length`, so the opponent's Spellbook/Atlas render as correctly-
// sized face-down stacks with zero card identities present in client memory.
//
// `playerNum === 2` rotates the piles to the opponent's side (the away seat).
// Mirrors spawnDeck's spawn-point + rotation contract so layout matches.
export function spawnOpponentCountStacks(counts, spawnPoints = {}, rotated = false) {
  const c = counts || {};
  const sb = spawnPoints.spellbook || { x: 30, z: 30 };
  const at = spawnPoints.atlas || { x: 30, z: -30 };

  const spellbookCount = Math.max(0, c.spellbook | 0);
  const atlasCount = Math.max(0, c.atlas | 0);

  const piles = [];
  const spellbookCards = Array.from({ length: spellbookCount }, () => createPlaceholderCard(rotated, false));
  piles.push(createPile(k('Spellbook'), spellbookCards, sb.x, sb.z, rotated));
  if (atlasCount > 0) {
    const atlasCards = Array.from({ length: atlasCount }, () => createPlaceholderCard(rotated, true));
    piles.push(createPile(k('Atlas'), atlasCards, at.x, at.z, rotated));
  }

  return { piles };
}

export function drawFromPile(state, pileId, from = 'top') {
  const pile = state.piles.find((p) => p.id === pileId);
  if (!pile || pile.cards.length === 0) return null;

  if (from === 'bottom') {
    const drawn = pile.cards[0];
    pile.cards = pile.cards.slice(1);
    return drawn;
  }
  const drawn = pile.cards[pile.cards.length - 1];
  pile.cards = pile.cards.slice(0, -1);
  return drawn;
}

export function shufflePile(state, pileId) {
  const pile = state.piles.find((p) => p.id === pileId);
  if (!pile) return;
  pile.cards = shuffleArray(pile.cards);
}
